import React, { useCallback } from 'react';
import { apiService } from '../../services/api';
import EntityHeader from './EntityHeader';
import KpiTiles from './KpiTiles';
import UsageLimitsBlock from './UsageLimitsBlock';
import ActivityCharts from './ActivityCharts';
import TokensBlock from './TokensBlock';
import MembersTable from './MembersTable';
import ConnectorsBlock, { PROVIDER_LABEL } from './ConnectorsBlock';
import GpuCostBlock from './GpuCostBlock';
import RunsTable from './RunsTable';
import { fmtCompact, fmtInt, fmtUsd } from './format';

const EntityPanel = ({ data, windowLabel, onSelect, refreshKey }) => {
  const { entity, limits, usage, counters: c, tokens, gpu, connectors, activity, series, members } = data;
  const isOrg = entity.type === 'organization';

  const fetchPage = useCallback((params) => {
    const call = isOrg ? apiService.qaOrganizationRuns : apiService.qaUserRuns;
    return call(entity.id, params).then((res) => res.data);
  }, [isOrg, entity.id]);

  const inWin = (n) => `${fmtInt(n)} in ${windowLabel}`;
  // "BigQuery ×2 · Snowflake" — the providers in use, not just a count.
  const providers = Object.entries(connectors.reduce((acc, c) => ({ ...acc, [c.type]: (acc[c.type] || 0) + 1 }), {}))
    .map(([type, n]) => `${PROVIDER_LABEL[type] || type}${n > 1 ? ` ×${n}` : ''}`).join(' · ');
  return (
    <>
      <EntityHeader entity={entity} limits={limits} activity={activity} onSelect={onSelect} />
      <KpiTiles tiles={[
        { key: 'fits', label: 'API fits', value: fmtInt(c.api_fits.total), sub: inWin(c.api_fits.window), icon: 'terminal' },
        { key: 'preds', label: 'API predictions & forecasts', value: fmtInt(c.api_predictions.total), sub: inWin(c.api_predictions.window), icon: 'line-chart' },
        { key: 'auto', label: 'Agent AutoRuns', value: fmtInt(c.agent_auto_runs.total), sub: inWin(c.agent_auto_runs.window), icon: 'bot' },
        { key: 'web', label: 'Web analyses', value: fmtInt(c.web_analyses.total), sub: inWin(c.web_analyses.window), icon: 'sparkles' },
        { key: 'tokens', label: 'Billed output tokens', value: fmtCompact(usage.all_time.output_tokens), sub: 'per-turn tokens the quota counts', icon: 'cpu' },
        { key: 'cost', label: 'Est. cost', value: fmtUsd(tokens.all_time.cost_usd + gpu.cost_usd), sub: `LLM ${fmtUsd(tokens.all_time.cost_usd)} · GPU ${fmtUsd(gpu.cost_usd)}`, icon: 'coins' },
        { key: 'pipes', label: 'Pipelines', value: `${fmtInt(c.pipelines.active)} / ${fmtInt(c.pipelines.schedules_total)}`, sub: `active / set up · ${fmtInt(c.pipelines.runs_total)} runs`, icon: 'workflow' },
        { key: 'failed', label: 'Failed', value: fmtInt(c.sdk_jobs_failed_window + c.pipelines.runs_failed_window + c.web_training_failed_window), sub: `API, pipeline and web training runs in ${windowLabel}`, icon: 'alert-triangle', tone: (c.sdk_jobs_failed_window + c.pipelines.runs_failed_window + c.web_training_failed_window) > 0 ? 'danger' : undefined },
        { key: 'keys', label: 'API keys', value: fmtInt(c.active_api_keys), sub: `${fmtInt(activity.active_sessions)} live SDK sessions`, icon: 'key-round' },
        { key: 'conn', label: 'Connectors', value: fmtInt(c.connectors.total), sub: c.connectors.error ? `${fmtInt(c.connectors.error)} with errors · ${providers}` : (providers || 'no warehouse connections'), icon: 'database', tone: c.connectors.error ? 'danger' : undefined },
      ]} />
      <UsageLimitsBlock usage={usage} limits={limits} counters={c} entity={entity} />
      <ActivityCharts series={series} />
      <TokensBlock tokens={tokens} />
      <div className="qa-two-col">
        <GpuCostBlock gpu={gpu} windowLabel={windowLabel} />
        <ConnectorsBlock connectors={connectors} />
      </div>
      {isOrg && <MembersTable members={members} onSelect={onSelect} />}
      <RunsTable fetchPage={fetchPage} refreshKey={refreshKey} showUser={isOrg} users={members} onSelectUser={(id) => onSelect({ type: 'user', id })} />
    </>
  );
};

export default EntityPanel;
